import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  RoutingRulesPaginatedQuery,
  useCreateRoutingRuleMutation,
  useUpdateRoutingRulePrioritiesMutation,
} from 'src/graphql';
import { createTimestampedName } from 'src/utils';

interface Parameters {
  setOpenIds: Dispatch<SetStateAction<string[]>>;
  isCOI?: boolean;
}

export const useMutateRoutingRules = ({
  setOpenIds,
  isCOI = false,
}: Parameters) => {
  const queryClient = useQueryClient();
  const [newRule, setNewRule] = useState<string | null>(null);
  const newRuleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (newRule) {
      const rule = newRuleRef.current;
      if (rule) {
        rule.scrollIntoView({ behavior: 'smooth' });
        setNewRule(null);
      }
    }
  }, [newRule]);

  const { mutate: createRoutingRule } = useCreateRoutingRuleMutation({
    onMutate: () => {
      setOpenIds([]);
    },
    onSuccess: (data) => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
      handleSuccessToast({
        toastMessage: 'New rule created',
      });
      setOpenIds([data?.createRoutingRule.id ?? '']);
      setNewRule(data.createRoutingRule.id);
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Error creating rule' });
    },
  });

  const createNewRule = () => {
    createRoutingRule({
      parameters: {
        name: createTimestampedName('Untitled Rule Name'),
        disabled: true,
        priority: 1,
        rule: {},
        isCOI,
      },
    });
  };

  const paginatedQueryKey = [
    'RoutingRulesPaginated',
    {
      pagination: { limit: 50, offset: 0 },
      orderBy: { fieldName: 'priority', direction: 'DESC' },
      isCOI,
    },
  ];

  const { mutate: updateRulePriorities } =
    useUpdateRoutingRulePrioritiesMutation({
      onMutate: async (variables) => {
        if (Array.isArray(variables.rules)) {
          await queryClient.cancelQueries(paginatedQueryKey);

          const snapshot =
            queryClient.getQueryData<RoutingRulesPaginatedQuery>(
              paginatedQueryKey,
            );
          const existingRules = snapshot?.routingRulesPaginated;

          const newOrderedRules = variables.rules.map((rule) => {
            const completeRule = existingRules?.items.find(
              (existingRule) => existingRule.id === rule.id,
            );
            return { ...completeRule, priority: rule.priority };
          });

          queryClient.setQueryData(paginatedQueryKey, {
            routingRulesPaginated: {
              count: existingRules?.count,
              items: newOrderedRules,
            },
          });

          return snapshot;
        }
      },
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: paginatedQueryKey });
        queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
      },
      onError: (_err, _vars, context) => {
        if (context) {
          queryClient.setQueryData(paginatedQueryKey, context);
        }
        handleErrorToast({ toastMessage: 'Error updating rule' });
      },
    });

  return {
    createNewRule,
    newRule,
    newRuleRef,
    updateRulePriorities,
  };
};
